import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

// Shared min-height (px, border-box) for every Section 1 card so the splash
// and all sub-steps render at the same size. Matches the tallest common state
// (full breakdown with history).
const CARD_MIN_HEIGHT = 502;

/**
 * Shared chrome for every Section 1 screen and the splash:
 * "Biological Age Analysis" pill + opaque rounded card.
 */
const BioAgeAnalysisCard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme: Theme = useTheme();
  return (
    <Box component="section" sx={styles.panel}>
      <Box sx={styles.titlePill(theme)}>Biological Age Analysis</Box>
      <Box sx={styles.card(theme)}>{children}</Box>
    </Box>
  );
};

export default BioAgeAnalysisCard;

const styles = {
  panel: {
    width: "100%",
    maxWidth: "1171px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "32px",
  },
  titlePill: (theme: Theme) => ({
    backgroundColor: `${theme.colors_ds.NEUTRAL_BG_MAIN}CC`,
    border: `1px solid rgba(0, 0, 0, 0.05)`,
    padding: "9px 17px",
    borderRadius: "24px",
    boxShadow:
      "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)",
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  card: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "16px",
    padding: "50px",
    boxShadow: "0px 25px 50px rgba(0, 0, 0, 0.05)",
    width: "100%",
    boxSizing: "border-box" as const,
    // Shared height across every Section 1 card (splash + all sub-steps) so the
    // container doesn't resize when moving from the splash into the stepper.
    // Content is vertically centred; taller content (e.g. the full breakdown)
    // grows past this floor as needed.
    minHeight: `${CARD_MIN_HEIGHT}px`,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "24px",
  }),
};
